import { GetStaticPaths, GetStaticProps } from 'next'
import { useRouter } from 'next/router'

import { SiteLocale } from '../../graphql/datoSchema.generated'
import stripUndefined from '../../utils/stripUndefined'
import notEmpty from '../../utils/notEmpty'
import Layout from '../../components/Layout'
import TextWithImageSection from '../../sections/TextWithImageSection'
import convertDatoImage from '../../utils/convertDatoImage'
import { BaseArticleFragment, getArticleList } from '../../graphql/articleList'
import RelatedArticlesSection from '../../sections/RelatedArticlesSection'
import { getSiteConfig, SiteConfig } from '../../graphql/siteConfig'
import { getLayout, Layout as LayoutType } from '../../graphql/layout'
import { getLocales, getSiteId } from '../../utils/env'
import { getExpert } from '../../graphql/expert'
import { ExpertDetailFragment } from '../../graphql/expert.generated'
import { getPage } from '../../graphql/page'
import { ExpertListingSectionFragment } from '../../graphql/expertListingSectionFragment.generated'
import ExpertProfileSection from '../../sections/ExpertProfileSection'
import Seo from '../../components/Seo'
import absolutify from '../../utils/absolutify'
import resolveUrlForRecord from '../../utils/resolveUrlForRecord'
import getAllPages from '../../utils/getAllPages'
import { getExpertList } from '../../graphql/expertList'
import { currentSite, Site } from '../../sites'

const getExpertSlugs = async () => {
  if (currentSite !== Site.EMN) {
    return []
  }

  const siteId = getSiteId()

  return (
    (
      await Promise.all(
        getLocales().map(async (locale) => {
          const expertListPages = await getAllPages(
            (page, pageSize) =>
              getExpertList(
                locale,
                undefined,
                undefined,
                undefined,
                undefined,
                pageSize,
                page,
                siteId,
              ),
            100,
          )
          const experts = expertListPages.map((page) => page.experts).flat()
          return experts.map(({ slug }) => ({
            slug,
            locale,
          }))
        }),
      )
    )
      .flat()
      // Remove pages without valid slug (this should never happen since it's required in the CMS)
      .filter(
        (page): page is typeof page & { slug: string } =>
          page.slug !== undefined && page.slug !== null,
      )
  )
}

export const getStaticPaths: GetStaticPaths = async () => {
  if (process.env.FEATUREFLAG_STATICPATHS !== 'true') {
    return {
      paths: [],
      fallback: 'blocking',
    }
  }

  return {
    paths: (await getExpertSlugs()).map(({ slug, locale }) => ({
      params: { slug },
      locale,
    })),
    fallback: 'blocking',
  }
}

export interface StaticProps {
  siteConfig: SiteConfig
  layout: LayoutType
  expert: ExpertDetailFragment
  expertListingSection: ExpertListingSectionFragment
  relatedArticles: BaseArticleFragment[]
}

export const getStaticProps: GetStaticProps<
  StaticProps,
  { slug: string | string[] }
> = async ({ params, locale }) => {
  if (!locale) {
    throw new Error('Next.js is misconfigured: locale is missing')
  }

  if (currentSite !== Site.EMN) {
    return {
      notFound: true,
      // TODO: Configure via environment variables
      revalidate: 60,
    }
  }

  const siteId = getSiteId()
  const slugPath = Array.isArray(params?.slug)
    ? params?.slug?.join('/').toLowerCase()
    : params?.slug?.toLowerCase()
  const slug = slugPath ?? ''
  if (!slug) {
    throw new Error('Missing required param: slug')
  }

  const [siteConfig, layout, expert] = await Promise.all([
    getSiteConfig(siteId, locale as SiteLocale),
    getLayout(siteId, locale as SiteLocale),
    getExpert(locale as SiteLocale, slug),
  ])

  // When unset, this site doesn't include an experts listing
  if (!siteConfig.expertsIndex?.slug) {
    return {
      notFound: true,
      // TODO: Configure via environment variables
      revalidate: 60,
    }
  }

  const expertsIndex = await getPage(
    siteId,
    locale as SiteLocale,
    siteConfig.expertsIndex.slug,
  )
  const expertListingSection = expertsIndex?.content.find(
    (
      block,
    ): block is ExpertListingSectionFragment & {
      id: string
      __typename: 'ExpertListingSectionRecord'
    } => block.__typename === 'ExpertListingSectionRecord',
  )

  if (!expertListingSection || !expert) {
    return {
      notFound: true,
      // TODO: Configure via environment variables
      revalidate: 60,
    }
  }

  const relatedArticlesCount = 3
  const relatedArticles = (
    await getArticleList(
      siteId,
      locale as SiteLocale,
      undefined,
      expert.id,
      relatedArticlesCount,
    )
  ).articles

  return {
    props: stripUndefined({
      siteConfig,
      layout,
      expert,
      expertListingSection,
      relatedArticles,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      path: `/${siteConfig.expertsIndex!.slug}/${slug}`,
    }),
    // TODO: Configure via environment variables
    revalidate: 60,
  }
}

const PageResolver = ({
  siteConfig,
  layout,
  expert,
  expertListingSection,
  relatedArticles,
}: StaticProps) => {
  const { locale, locales } = useRouter()
  if (!locale || !locales) {
    throw new Error('locale or locales is missing')
  }
  if (!expert.slug) {
    throw new Error('Expert.slug is missing')
  }
  if (!expert.name) {
    throw new Error('Expert.name is missing')
  }
  if (!expert.photo) {
    throw new Error('Expert.photo is missing')
  }

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const canonicalPath = `/${siteConfig.expertsIndex!.slug}/${expert.slug}`

  return (
    <Layout
      siteConfig={siteConfig}
      layout={layout}
      breadcrumbs={[
        {
          key: 'homepage',
          href: '/',
          title: siteConfig.homepageName || 'Home',
        },
        {
          key: 'news',
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          href: `/${siteConfig.expertsIndex!.slug}/`,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          title: siteConfig.expertsIndex!.seo?.title || 'Experts',
        },
        {
          key: 'expert',
          href: canonicalPath,
          title: expert.name,
        },
      ]}
    >
      <Seo
        title={expert.name}
        description={expert.about || undefined}
        canonical={absolutify(canonicalPath, locale as SiteLocale)}
        paths={locales.reduce((obj, locale) => {
          // eslint-disable-next-line no-param-reassign
          obj[locale as SiteLocale] = resolveUrlForRecord(
            siteConfig,
            {
              __typename: 'ExpertRecord',
              slug: expert.slug,
            },
            locale as SiteLocale,
          )
          return obj
        }, {} as Record<SiteLocale, string>)}
      />

      <TextWithImageSection
        title={expert.name}
        titleVariant="pageTitle"
        text={expert.about}
        imageSize="landscape"
        variant="header"
      />
      <ExpertProfileSection
        name={expert.name}
        photo={convertDatoImage(expert.photo)}
        email={expert.email || undefined}
        phone={expert.phone || undefined}
        linkedIn={expert.linkedIn || undefined}
        disciplines={expert.disciplines
          .map(({ name }) => name)
          // If a discipline doesn't have a value for the current locale,
          // name may be an empty string
          .filter((name) => name !== '')
          .filter(notEmpty)}
        specialisms={expert.specialisms
          .map(({ name }) => name)
          // If a specialism doesn't have a value for the current locale,
          // name may be an empty string
          .filter((name) => name !== '')
          .filter(notEmpty)}
        qualifications={expert.qualifications || undefined}
        experience={expert.experience || undefined}
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        getInTouchLabel={expertListingSection.getInTouch!}
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        linkedInProfileLabel={expertListingSection.linkedInProfile!}
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        disciplinesLabel={expertListingSection.disciplines!}
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        specialismsLabel={expertListingSection.specialisms!}
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        qualificationsLabel={expertListingSection.qualifications!}
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        experienceLabel={expertListingSection.experience!}
      />
      <RelatedArticlesSection
        title={
          expertListingSection.relatedArticles?.replace(
            '{name}',
            expert.name,
          ) || undefined
        }
        articles={relatedArticles
          .map(({ slug, title, articleType, image, brief }) => {
            if (
              !slug ||
              !title ||
              !articleType?.title ||
              !articleType.color ||
              !image ||
              !brief
            ) {
              return undefined
            }

            return {
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              href: `/${siteConfig.newsIndex!.slug}/${slug}`,
              title,
              type: articleType.title,
              typeColor: articleType.color,
              image: convertDatoImage(image),
              brief,
            }
          })
          .filter(notEmpty)}
      />
    </Layout>
  )
}

export default PageResolver
